import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "@/components/yc/Nav";
import { MapPin } from "lucide-react";

const ROTATING_WORDS = ["videographers.", "photographers.", "storytellers.", "UGC creators.", "filmmakers."];

const DISCIPLINES = [
    { code: "SF", title: "Short-Form Video", desc: "Scroll-stopping vertical content built for audiences that won't wait.", pills: ["TikTok", "Reels", "Shorts"] },
    { code: "PH", title: "Photography", desc: "Lifestyle, product, editorial — imagery that carries a brand's visual language.", pills: ["Lifestyle", "Product", "Editorial"] },
    { code: "LF", title: "Long-Form Video", desc: "Produced video with narrative depth — content that earns its watch time.", pills: ["YouTube", "Brand Film", "Doc"] },
    { code: "UG", title: "UGC Content", desc: "Authentic, platform-native content designed to feel real — because it is.", pills: ["TikTok", "Reels", "Reviews"] },
    { code: "FB", title: "Fashion & Beauty", desc: "Aesthetic-led creators who understand editorial standards and brand fit.", pills: ["Instagram", "TikTok", "Lookbook"] },
    { code: "FH", title: "Food & Hospitality", desc: "Restaurant, product, and lifestyle food content that makes people act.", pills: ["Instagram", "TikTok", "YouTube"] },
    { code: "FW", title: "Fitness & Wellness", desc: "High-energy creators who move audiences as well as they move themselves.", pills: ["TikTok", "YouTube", "Instagram"] },
    { code: "TL", title: "Tech & Lifestyle", desc: "Creators who make a technical product feel human and worth wanting.", pills: ["YouTube", "TikTok", "Shorts"] },
    { code: "TC", title: "Travel & Culture", desc: "Roving creators with a strong visual eye and an audience that actually travels.", pills: ["Instagram", "YouTube", "TikTok"] },
    { code: "B2B", title: "B2B & Corporate", desc: "Professionals who understand business audiences — not just consumer ones.", pills: ["LinkedIn", "YouTube", "Brand Film"] },
];

const LOCATIONS = [
    "Leeds", "Manchester", "London", "Yorkshire", "Sheffield", "Midlands",
    "Cambridgeshire", "East Anglia", "Birmingham", "Liverpool", "Bristol", "Newcastle",
];

const LocPill = ({ name }) => (
    <div className="flex items-center gap-2 px-4 py-2.5 border-2 border-yc-ink rounded-full bg-white text-sm font-semibold whitespace-nowrap hover:bg-yc-ink hover:text-white transition-colors">
        <MapPin className="w-3 h-3 shrink-0" /> {name}
    </div>
);

const Team = () => {
    const [wordIdx, setWordIdx] = useState(0);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setVisible(false);
            setTimeout(() => {
                setWordIdx((i) => (i + 1) % ROTATING_WORDS.length);
                setVisible(true);
            }, 180);
        }, 2400);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-yc-cream">
            <Navbar />

            <style>{`
                @keyframes team-marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .team-marquee-track {
                    animation: team-marquee 22s linear infinite;
                    width: max-content;
                }
                .team-marquee-track:hover { animation-play-state: paused; }
            `}</style>

            <div className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
                {/* HERO */}
                <section className="pt-16 md:pt-20 pb-12">
                    <div className="yc-overline">Team Chaos</div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.0] mt-5">
                        The UK&apos;s vetted
                        <br />
                        <span className="inline-block bg-yc-blue text-white px-3 pb-1 leading-[1.1]">
                            <span
                                className="inline-block transition-all duration-200"
                                style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(10px)" }}
                                data-testid="team-rotating-word"
                            >
                                {ROTATING_WORDS[wordIdx]}
                            </span>
                        </span>
                    </h1>
                    <p className="text-lg text-yc-ink/70 mt-7 leading-relaxed max-w-xl">
                        From Leeds to London — every discipline, one standard. Our creators are picked, not selected.
                    </p>
                </section>

                {/* VIDEO */}
                <section className="pt-2">
                    <div className="mb-7">
                        <div className="yc-overline">In The Field</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-2.5 leading-tight">See what vetted looks like.</h2>
                        <p className="text-base text-yc-ink/70 mt-2.5 leading-relaxed max-w-xl">
                            This is the standard every creator in our network is held to — on every brief, every time.
                        </p>
                    </div>
                    <div className="border-2 border-yc-ink rounded-3xl overflow-hidden relative bg-yc-ink" style={{ paddingTop: "56.25%" }} data-testid="team-video">
                        <iframe
                            className="absolute inset-0 w-full h-full"
                            src="https://www.youtube.com/embed/6IJb1BLYaFs?rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&color=white"
                            title="Curated Chaos — In The Field"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </section>

                <div className="h-0.5 bg-yc-ink rounded my-14" />

                {/* DISCIPLINES */}
                <section>
                    <div className="mb-8">
                        <div className="yc-overline">What We Do</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-2.5 leading-tight">Every discipline. One standard.</h2>
                        <p className="text-base text-yc-ink/70 mt-2.5 leading-relaxed max-w-xl">
                            Our creators specialise across every major content format — vetted to the same bar regardless of their medium. Hover to explore.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="team-disciplines-grid">
                        {DISCIPLINES.map((d) => (
                            <div
                                key={d.code}
                                className="group border-2 border-yc-ink rounded-2xl p-6 bg-white transition-all duration-200 hover:bg-yc-blue hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-[5px_5px_0_0_#0D0D0D]"
                                data-testid={`team-discipline-${d.code.toLowerCase()}`}
                            >
                                <div className="w-9 h-9 rounded-[10px] bg-yc-cream text-yc-blue text-sm font-black flex items-center justify-center mb-3.5 transition-colors group-hover:bg-white/15 group-hover:text-white">
                                    {d.code}
                                </div>
                                <div className="text-[15px] font-bold mb-1.5 text-yc-ink transition-colors group-hover:text-white">{d.title}</div>
                                <div className="text-[13px] text-yc-ink/65 leading-relaxed mb-3.5 transition-colors group-hover:text-white">{d.desc}</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {d.pills.map((p) => (
                                        <span key={p} className="text-[10px] font-bold tracking-wider px-2 py-0.5 border-[1.5px] border-yc-ink/20 rounded-full text-yc-ink/60 transition-colors group-hover:border-white/35 group-hover:text-white/85">
                                            {p}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="h-0.5 bg-yc-ink rounded my-14" />

                {/* LOCATIONS */}
                <section>
                    <div className="mb-8">
                        <div className="yc-overline">Where We Are</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-2.5 leading-tight">UK-wide. Always local.</h2>
                        <p className="text-base text-yc-ink/70 mt-2.5 leading-relaxed max-w-xl">
                            Our network covers the full country — wherever the campaign needs to be, someone&apos;s already there.
                        </p>
                    </div>

                    <div className="overflow-hidden -mx-6 md:-mx-10 px-6 md:px-10" data-testid="team-locations-marquee">
                        <div className="team-marquee-track flex gap-2.5">
                            {[...LOCATIONS, ...LOCATIONS].map((l, i) => (
                                <LocPill key={`${l}-${i}`} name={l} />
                            ))}
                        </div>
                    </div>
                </section>

                <div className="h-0.5 bg-yc-ink rounded my-14" />

                {/* JOIN */}
                <section>
                    <div className="border-2 border-yc-ink rounded-3xl p-10 md:p-12 bg-yc-pink text-white flex flex-wrap items-center justify-between gap-8">
                        <div>
                            <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-white/60 mb-3">Think You Belong?</div>
                            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight">Applications are open.</h2>
                            <p className="text-base text-white/85 mt-3 max-w-md leading-relaxed">
                                We review every creator personally. If you&apos;ve put in the work and you know your craft, we want to see it.
                            </p>
                        </div>
                        <Link
                            to="/login?role=creator"
                            data-testid="team-apply-cta"
                            className="inline-block px-8 py-4 bg-white text-yc-ink border-2 border-yc-ink rounded-full font-bold text-[15px] shadow-[3px_3px_0_0_#0D0D0D] hover:shadow-[1px_1px_0_0_#0D0D0D] hover:translate-x-[2px] hover:translate-y-[2px] transition-all whitespace-nowrap"
                        >
                            Apply as a creator →
                        </Link>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default Team;
